import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSetWidgetAttribute } from '../../../hooks/useSetWidgetAttribute.js';
import { useFormulaFields, FORMULA_ACCESSOR_PREFIX } from '../../../hooks/useFormulaFields.js';

// Series attributes that can point to a column (and therefore to a formula field)
const SERIES_COLUMN_KEYS = [
  'xKey', 'yKey', 'sizeKey', 'angleKey', 'radiusKey',
  'labelKey', 'calloutLabelKey', 'sectorLabelKey', 'legendItemKey',
];

const EMPTY_SERIES = [];
const EMPTY_DATASOURCES = [];

const generateId = () => Math.random().toString(36).substring(2, 9);

export const useFormulaFieldSettings = (widgetId) => {
  const setAttribute = useSetWidgetAttribute(widgetId);
  const { formulaFields } = useFormulaFields(widgetId);
  const series = useSelector(state => state.dashboard.widgets[widgetId]?.settings?.series ?? EMPTY_SERIES);
  const axes = useSelector(state => state.dashboard.widgets[widgetId]?.settings?.chartOptions?.axes);
  // Data strip items point to a column via colId
  const datasources = useSelector(state => state.dashboard.widgets[widgetId]?.settings?.datasources ?? EMPTY_DATASOURCES);

  // Returns the created field so callers can select its accessor right away
  const addFormulaField = useCallback(({ name, formula }) => {
    const id = generateId();
    const field = { id, accessor: `${FORMULA_ACCESSOR_PREFIX}${id}`, name, formula };
    setAttribute('settings.formulaFields', [...formulaFields, field]);
    return field;
  }, [formulaFields, setAttribute]);

  const updateFormulaField = useCallback((id, { name, formula }) => {
    setAttribute(
      'settings.formulaFields',
      formulaFields.map(f => (f.id === id ? { ...f, name, formula } : f))
    );
  }, [formulaFields, setAttribute]);

  // Also clears every series key, axis key and data strip item colId that points to the removed field
  const removeFormulaField = useCallback((id) => {
    const field = formulaFields.find(f => f.id === id);
    if (!field) return;

    setAttribute('settings.formulaFields', formulaFields.filter(f => f.id !== id));

    if (series.some(s => SERIES_COLUMN_KEYS.some(key => s[key] === field.accessor))) {
      const cleanedSeries = series.map(s => {
        const copy = { ...s };
        SERIES_COLUMN_KEYS.forEach(key => {
          if (copy[key] === field.accessor) delete copy[key];
        });
        return copy;
      });
      setAttribute('settings.series', cleanedSeries);
    }

    if (datasources.some(d => d.colId === field.accessor)) {
      setAttribute(
        'settings.datasources',
        datasources.map(d => (d.colId === field.accessor ? { ...d, colId: '' } : d))
      );
    }

    if (Array.isArray(axes)) {
      setAttribute(
        'settings.chartOptions.axes',
        axes.map(a => (Array.isArray(a.keys) ? { ...a, keys: a.keys.filter(k => k !== field.accessor) } : a))
      );
    }
  }, [formulaFields, series, axes, datasources, setAttribute]);

  return { formulaFields, addFormulaField, updateFormulaField, removeFormulaField };
};
